/**
 * MetricCard - Reusable metric display card
 *
 * Displays a single metric with icon, label, value, and optional trend indicator.
 * Used for dashboard statistics and key performance indicators.
 */
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  description?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
    label?: string;
  };
  valueColor?: string;
  iconColor?: string;
  className?: string;
}

/**
 * MetricCard component
 *
 * @example
 * <MetricCard
 *   icon={Activity}
 *   label="Total Executions"
 *   value={156}
 *   description="Last 24 hours"
 *   trend={{ value: 12, direction: 'up', label: '+12% from yesterday' }}
 * />
 */
export function MetricCard({
  icon: Icon,
  label,
  value,
  description,
  trend,
  valueColor = 'text-foreground',
  iconColor = 'text-muted-foreground',
  className,
}: MetricCardProps) {
  const TrendIcon = trend
    ? trend.direction === 'up'
      ? TrendingUp
      : trend.direction === 'down'
      ? TrendingDown
      : Minus
    : null;

  const trendColor =
    trend?.direction === 'up'
      ? 'text-success'
      : trend?.direction === 'down'
      ? 'text-destructive'
      : 'text-muted-foreground';

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Label */}
            <p className="text-sm font-medium text-muted-foreground mb-1">
              {label}
            </p>

            {/* Value */}
            <div className="flex items-baseline gap-2 mb-1">
              <p className={cn('text-3xl font-bold', valueColor)}>
                {typeof value === 'number' ? value.toLocaleString('en-US') : value}
              </p>

              {/* Trend Indicator */}
              {trend && TrendIcon && (
                <div className={cn('flex items-center gap-1 text-sm', trendColor)}>
                  <TrendIcon className="h-4 w-4" aria-hidden="true" />
                  <span className="font-medium">
                    {trend.value > 0 ? '+' : ''}
                    {trend.value}%
                  </span>
                </div>
              )}
            </div>

            {/* Description or Trend Label */}
            {(description || trend?.label) && (
              <p className="text-xs text-muted-foreground">
                {trend?.label || description}
              </p>
            )}
          </div>

          {/* Icon */}
          <div
            className={cn(
              'rounded-lg bg-muted/50 p-3',
              iconColor
            )}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Skeleton loader for MetricCard
 */
export function MetricCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
            <div className="h-8 w-20 bg-muted animate-pulse rounded" />
            <div className="h-3 w-32 bg-muted animate-pulse rounded" />
          </div>
          <div className="h-11 w-11 bg-muted animate-pulse rounded-lg" />
        </div>
      </CardContent>
    </Card>
  );
}
